import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import PropTypes from 'prop-types';
import { cache, cacheGet } from '../util/cache';

const ThemeContext = createContext();

const currModeKey = 'setting.theme.mode';

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

export const CustomThemeProvider = ({ children }) => {
  // Get initial mode from localStorage or default to 'light'
  const [mode, setMode] = useState(() => cacheGet(currModeKey, false, 'light'));

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    cache(currModeKey, mode);
  }, [mode]);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        light: '#4ca2df',
        main: '#3a3d82',
        dark: '#1e2050',
      },
      secondary: {
        light: '#aedfff',
        main: '#aedfff',
        dark: '#7bb3cc',
      },
      // Custom colors for light/dark modes
      ...(mode === 'light'
        ? {
            // Light mode colors
            background: {
              default: '#fafafa',
              paper: '#ffffff',
            },
            text: {
              primary: '#1a202c',
              secondary: '#4a5568',
            },
            // Link colors for light mode
            action: {
              ...createTheme().palette.action,
              linkDefault: '#3a3d82', // Using your primary color
              linkHover: '#1e2050', // Using your primary dark color
            },
          }
        : {
            // Dark mode colors
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: '#ffffff',
              secondary: '#b3b3b3',
            },
            // Link colors for dark mode
            action: {
              ...createTheme().palette.action,
              linkDefault: '#4ca2df', // Using your primary light color
              linkHover: '#aedfff', // Using your secondary color
            },
          }),
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      // Custom component overrides for both modes
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            ...(mode === 'dark' && {
              backgroundImage: 'none',
            }),
          },
        },
      },
      // Global link styles using CSS variables
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--tblr-link-color': mode === 'light' ? '#3a3d82' : '#4ca2df',
            '--tblr-link-hover-color': mode === 'light' ? '#1e2050' : '#aedfff',
          },
        },
      },
    },
  });

  const value = {
    mode,
    toggleColorMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

CustomThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
